import {
  FieldShell, TextInput, NumberInput, SelectInput, CheckboxRow,
  TagsInput, PeopleList, CountrySplit, CheckboxGroup, VALIDATORS,
} from "./fields";
import { COUNTRY_OPTIONS } from "./referenceData";

/**
 * Schema-driven step renderer. For 5/7 steps this is all we need — Step 7
 * (review) and Step 6 (documents placeholder) have their own components.
 *
 * onPatch(field, value) → step layer accumulates and queues the auto-save.
 */
export default function GenericStep({ schema, value, onPatch, flushNow, testIdPrefix }) {
  if (!schema) return null;
  const fields = schema.fields || {};

  const handleChange = (key) => (v) => onPatch({ [key]: v });

  return (
    <div className="space-y-5">
      {Object.entries(fields).map(([key, spec]) => {
        const v = value?.[key];
        const tid = `${testIdPrefix}-${key}`;
        const common = { value: v, onChange: handleChange(key), onBlur: flushNow, testId: `${tid}-input` };

        return (
          <FieldShell key={key} label={spec.label} required={spec.required} testId={tid}>
            {renderField(spec, v, common, key, handleChange, tid)}
          </FieldShell>
        );
      })}
    </div>
  );
}

function renderField(spec, v, common, key, handleChange, tid) {
  switch (spec.type) {
    case "string":
      if (spec.enum) return <SelectInput {...common} options={spec.enum} />;
      // textarea heuristic — long descriptions get a multiline box.
      if (spec.max_length && spec.max_length >= 1000)
        return <TextInput {...common} multiline rows={6} />;
      // Sub-task heuristics — give name fields the full-name validator so
      // single-word inputs like "D Bouropoulos" are caught on blur.
      if (key === "primary_contact_name") {
        return <TextInput {...common} validate={VALIDATORS.fullName} />;
      }
      return <TextInput {...common} />;
    case "email":
      return <TextInput {...common} type="email" validate={VALIDATORS.email} />;
    case "url":
      return <TextInput {...common} type="url" placeholder="https://" validate={VALIDATORS.url} />;
    case "phone":
      return <TextInput {...common} type="tel" placeholder="+33 …" validate={VALIDATORS.phone} />;
    case "date":
      return <TextInput {...common} type="date" />;
    case "int":
    case "number":
      return <NumberInput {...common} step={spec.type === "int" ? 1 : "any"} />;
    case "bool":
      return (
        <CheckboxRow
          checked={!!v}
          onChange={(checked) => { handleChange(key)(checked); }}
          label={spec.label}
          testId={`${tid}-checkbox`}
        />
      );
    case "array_of_string":
      if (spec.item_enum) {
        return (
          <CheckboxGroup
            value={v}
            onChange={handleChange(key)}
            options={spec.item_enum}
            testId={`${tid}-checkboxgroup`}
          />
        );
      }
      return <TagsInput value={v} onChange={handleChange(key)} placeholder="Type and press Enter" testId={`${tid}-tags`} />;
    case "people_list":
      return (
        <PeopleList
          value={v}
          onChange={handleChange(key)}
          itemFields={spec.item_fields}
          addLabel={key === "ubos" ? "Add UBO" : "Add director"}
          testId={`${tid}-people`}
        />
      );
    case "country_split":
      return (
        <CountrySplit
          value={v}
          onChange={handleChange(key)}
          testId={`${tid}-countries`}
          countryOptions={COUNTRY_OPTIONS}
        />
      );
    default:
      return <TextInput {...common} />;
  }
}
